import { useQuery } from '@tanstack/react-query'
import type { ReactNode } from 'react'

import { useAuth } from '../../AuthContext'
import {
  SERVICE_NAMES,
  TRANSPORT_CONFIG_KEY,
  fetchFares,
  type Fare,
} from '../../api/transportConfig'
import { FareCell } from './FareCell'
import { SectionState } from './SectionState'
import { retryUnlessForbidden } from './errorMessage'

type Item =
  | 'leg'
  | 'half_day'
  | 'full_day'
  | 'overtime_hour'
  | 'wheelchair'
  | 'platform_fee'
  | 'admin_fee'

const ITEM_NAMES: Record<Item, string> = {
  leg: 'One leg (each way)',
  half_day: 'Half day',
  full_day: 'Full day',
  overtime_hour: 'Overtime, per hour',
  wheelchair: 'Wheelchair supplied',
  platform_fee: 'Platform fee',
  admin_fee: 'Admin fee (cancellations)',
}

const ZONES = [1, 2, 3] as const

/** The zoned groups, one table each, in the order the booking screen shows
 * them. Executive has no fares: it is booked by phone (owner Q7). */
const GROUPS = [
  { service: 'standard_ride', items: ['leg'] },
  { service: 'pts', items: ['leg'] },
  { service: 'car_hire', items: ['half_day', 'full_day', 'overtime_hour'] },
] as const

const SAME_EVERYWHERE = ['platform_fee', 'wheelchair', 'admin_fee'] as const

/**
 * Fares by service and zone (spec §C.3.2, §E.2). Every fare starts "Not set",
 * and a service stays not bookable until its required fares are set. A fare
 * is set one cell at a time.
 */
export function FaresSection(): ReactNode {
  const { token } = useAuth()
  const query = useQuery({
    queryKey: [...TRANSPORT_CONFIG_KEY, 'fares'],
    queryFn: () => fetchFares(token),
    enabled: Boolean(token),
    retry: retryUnlessForbidden,
  })
  const state = (
    <SectionState
      isPending={query.isPending}
      error={query.error}
      onRetry={() => void query.refetch()}
      what="fares"
    />
  )
  if (!query.data) return state

  const shown = (f: Fare): boolean =>
    GROUPS.some(
      (g) =>
        g.service === f.service &&
        (g.items as readonly string[]).includes(f.item) &&
        f.zone !== null,
    ) ||
    (f.service === 'all' &&
      f.zone === null &&
      (SAME_EVERYWHERE as readonly string[]).includes(f.item))
  // Any other row (a future item) is listed below rather than hidden, so
  // nothing can block booking without appearing on this page.
  const others = query.data.filter((f) => !shown(f))

  const find = (
    service: string,
    item: string,
    zone: number | null,
  ): Fare | undefined =>
    query.data.find(
      (f) => f.service === service && f.item === item && f.zone === zone,
    )

  return (
    <div className="flex flex-col gap-6">
      <p className="text-sm text-ink-3">
        Fixed fares by zone. A trip&apos;s zone is the higher of its pickup
        area&apos;s and its hospital&apos;s; Car Hire is priced on the pickup
        zone alone. Leave a fare empty for &ldquo;not set&rdquo;.
      </p>

      {GROUPS.map((group) => (
        <div key={group.service} className="overflow-x-auto">
          <table className="w-full min-w-[28rem] border-collapse text-sm">
            <caption className="mb-2 text-left font-medium text-ink-1">
              {SERVICE_NAMES[group.service]}
            </caption>
            <thead>
              <tr className="text-left text-xs text-ink-3">
                <th scope="col" className="py-1 pr-3 font-medium">
                  Item
                </th>
                {ZONES.map((z) => (
                  <th key={z} scope="col" className="py-1 pr-3 font-medium">
                    Zone {z}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {group.items.map((item) => (
                <tr key={item} className="border-t border-line-1 align-top">
                  <th
                    scope="row"
                    className="py-2 pr-3 text-left font-normal text-ink-2"
                  >
                    {ITEM_NAMES[item]}
                  </th>
                  {ZONES.map((z) => {
                    const fare = find(group.service, item, z)
                    return (
                      <td key={z} className="py-2 pr-3">
                        {fare ? (
                          <FareCell
                            fare={fare}
                            label={`${SERVICE_NAMES[group.service]}, ${ITEM_NAMES[item]}, zone ${z}`}
                          />
                        ) : (
                          <span className="text-xs text-ink-3">
                            No fare row
                          </span>
                        )}
                      </td>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <caption className="mb-2 text-left font-medium text-ink-1">
            Every service, every zone
          </caption>
          <tbody>
            {SAME_EVERYWHERE.map((item) => {
              const fare = find('all', item, null)
              return (
                <tr key={item} className="border-t border-line-1 align-top">
                  <th
                    scope="row"
                    className="py-2 pr-3 text-left font-normal text-ink-2"
                  >
                    {ITEM_NAMES[item]}
                  </th>
                  <td className="py-2">
                    {fare ? (
                      <FareCell fare={fare} label={ITEM_NAMES[item]} />
                    ) : (
                      <span className="text-xs text-ink-3">No fare row</span>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      {others.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="mb-2 text-left font-medium text-ink-1">
              Other fares
            </caption>
            <tbody>
              {others.map((fare) => {
                const label = `${fare.service}, ${fare.item}${fare.zone === null ? '' : `, zone ${fare.zone}`}`
                return (
                  <tr
                    key={fare.id}
                    className="border-t border-line-1 align-top"
                  >
                    <th
                      scope="row"
                      className="py-2 pr-3 text-left font-normal text-ink-2"
                    >
                      {label}
                    </th>
                    <td className="py-2">
                      <FareCell fare={fare} label={label} />
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
